import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';

import { BodyText, Button, Modal, theme, useResponsive } from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationRequiredModificationModal = (props: Props) => {
  const {
    isVisible,
    productName,
    requiredIngredientName,
    body,
    buttonTitle,
    onConfirm,
    onShow,
    size,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const ingredientName = (
    requiredIngredientName ?? formatMessage(messages.ingredient)
  ).toLowerCase();

  const formattedBody = body
    ? body
        .replaceAll(DYNAMIC_INGREDIENT_TEXT, ingredientName)
        .replaceAll(DYNAMIC_PRODUCT_TEXT, productName)
    : formatMessage(messages.body, { ingredient: ingredientName, product: productName });

  const formattedButtonTitle = buttonTitle ?? formatMessage(messages.gotIt);

  // ─── Styles ──────────────────────────────────────────────────────────

  const ctaStyles = size === 'lg' ? styles.ctaLG : styles.ctaXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onConfirm}
    >
      <CustomizationModalContentContainer size={size ?? match(['md', 'lg'])}>
        <BodyText sizeMatch={['22']}>{formattedBody}</BodyText>

        <View style={styles.ctaContainer}>
          <Button size="large-wide" palette="spinachMuted" style={ctaStyles} onPress={onConfirm}>
            {formattedButtonTitle}
          </Button>
        </View>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DYNAMIC_INGREDIENT_TEXT = '(ingredient)';
const DYNAMIC_PRODUCT_TEXT = '(product)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalLabel: {
    defaultMessage: 'Required ingredient modal',
    description: 'Customization | Required modification modal | Label',
  },
  body: {
    defaultMessage: 'This {ingredient} cannot be removed from the {product}.',
    description: 'Customization | Required modification modal | Body',
  },
  ingredient: {
    defaultMessage: 'ingredient',
    description: 'Customization | Required modification modal | Ingredient',
  },
  product: {
    defaultMessage: 'product',
    description: 'Customization | Required modification modal | Product',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Customization | Required modification modal | Got it button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['2'],
    paddingBottom: Platform.select({
      android: theme.spacing['2'],
      default: 0,
    }),
  },
  ctaXS: {
    paddingHorizontal: theme.spacing['16'],
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
  ctaLG: {
    minHeight: 72,
    paddingHorizontal: theme.spacing['16'],
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  productName: string;
  requiredIngredientName: string | undefined;
  body?: string;
  buttonTitle?: string;
  onConfirm: () => void;
  onShow?: () => void;
  size?: 'md' | 'lg';
};
